import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { PatientAlertsTableContent } from "../../../../@types/Patient";
import { ReactComponent as ErrorCheckingAlertsImage } from "../../../../assets/images/error-checking-alerts.svg";
import { ReactComponent as NoAlertTriggeredImage } from "../../../../assets/images/no-alert-triggered.svg";
import { InfiniteTable } from "../../../../components/InfiniteTable";
import { RetryButton } from "../../../../components/RetryButton";
import { Table } from "../../../../components/Table";
import { TableSkeleton } from "../../../../components/TableSkeleton";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { ResolveTriggerModal } from "../ResolveTriggerModal";
import { ManageTriggersModal } from "./components/ManageTriggersModal";
import {
  AlertsTableContainer,
  AlertsTableInnerContainer,
  ImageContainer,
  ManageTriggersTitle,
  NoContentMessage,
} from "./PatientAlertsTable.style";

type PatientAlertTableProps = {
  patientId: number;
  columns: ColumnDef<any, string>[];
  content: PatientAlertsTableContent[];
  isLoading?: boolean;
  isError?: boolean;
  updateDropdown: () => void;
  refreshTable: () => void;
  showManageTriggersButton: boolean;
  // Infinite scroll props (optional for backward compatibility)
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  useInfiniteScroll?: boolean;
};

export function PatientAlertsTable({
  patientId,
  isLoading,
  isError,
  content,
  showManageTriggersButton,
  updateDropdown,
  refreshTable,
  columns,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  useInfiniteScroll = false,
}: PatientAlertTableProps) {
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [highlightedRows, setHighlightedRows] = useState<any>([]);

  useEffect(() => {
    const rows: any[] = [];

    if (content && content.length > 0) {
      content.forEach((row) => {
        rows.push(row.isActive);
      });
    }

    setHighlightedRows(rows);
  }, [content]);

  const handleCloseModal = (type: string, success?: boolean) => {
    if (success) {
      showToast(t("patientPage_alerts_resolvedToast", { type }), "success");

      mixpanelActions.track("User Action: Resolve Alert");

      updateDropdown();
      refreshTable();
      queryClient.refetchQueries({
        queryKey: [`patient-notes-table-${patientId}`, patientId],
      });
    } else if (!success) {
      showToast(t("patientPage_alerts_resolveFailed", { type }), "error");
    }

    closeModal();
  };

  const handleRowClick = (patientAlertContent: PatientAlertsTableContent) => {
    if (patientAlertContent.isActive) {
      openModal(
        <ResolveTriggerModal
          patientId={patientId}
          alertIds={[patientAlertContent.id]}
          type={patientAlertContent.type}
          triggered={patientAlertContent.timeOfAlert.toString()}
          onClose={(success) =>
            handleCloseModal(patientAlertContent.type, success)
          }
        />,
        {
          width: "auto",
          height: "auto",
          showCloseButton: true,
        }
      );
    }
  };

  const handleChildNotification = (
    notificationMessage: string,
    notificationType: "success" | "warning" | "error"
  ) => {
    if (notificationMessage && notificationType) {
      showToast(notificationMessage, notificationType);
    }
  };

  const handleManageTriggers = () => {
    openModal(
      <ManageTriggersModal
        setNotification={handleChildNotification}
        patientId={patientId}
      />,
      {
        width: "100%",
        height: "100%",
        showCloseButton: true,
      }
    );
  };

  if (isLoading) {
    return (
      <AlertsTableContainer style={{ overflow: "hidden" }}>
        <AlertsTableInnerContainer style={{ overflow: "hidden" }}>
          <TableSkeleton columnsNumber={7} rowsNumber={8} />
        </AlertsTableInnerContainer>
      </AlertsTableContainer>
    );
  }

  if (isError) {
    return (
      <ImageContainer>
        <ErrorCheckingAlertsImage />
        <p>
          <Trans
            i18nKey="patientPage_alerts_loadError"
            components={{
              retry: <RetryButton onClick={refreshTable} />,
            }}
          />
        </p>
      </ImageContainer>
    );
  }

  if (content && content.length > 0) {
    // Use InfiniteTable when infinite scroll is enabled
    if (useInfiniteScroll && fetchNextPage) {
      return (
        <>
          {showManageTriggersButton && (
            <ManageTriggersTitle onClick={handleManageTriggers}>
              {t("patientPage_triggers_manage_title")}
            </ManageTriggersTitle>
          )}
          <AlertsTableContainer>
            <AlertsTableInnerContainer>
              <InfiniteTable
                data={content}
                columns={columns}
                onRowClick={handleRowClick}
                highlightedRows={highlightedRows}
                highlightColor={[
                  "rgba(250, 209, 217, 0.48)",
                  "rgba(250, 209, 217, 0.28)",
                ]}
                rowPadding
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                skeletonColumns={7}
                skeletonRows={8}
              />
            </AlertsTableInnerContainer>
          </AlertsTableContainer>
        </>
      );
    }

    // Default to regular Table for backward compatibility
    return (
      <>
        {showManageTriggersButton && (
          <ManageTriggersTitle onClick={handleManageTriggers}>
            {t("patientPage_triggers_manage_title")}
          </ManageTriggersTitle>
        )}
        <AlertsTableContainer>
          <AlertsTableInnerContainer>
            <Table
              data={content}
              columns={columns}
              onRowClick={handleRowClick}
              highlightedRows={highlightedRows}
              highlightColor={[
                "rgba(250, 209, 217, 0.48)",
                "rgba(250, 209, 217, 0.28)",
              ]}
              rowPadding
            />
          </AlertsTableInnerContainer>
        </AlertsTableContainer>
      </>
    );
  }

  return (
    <>
      {showManageTriggersButton && (
        <ManageTriggersTitle onClick={handleManageTriggers}>
          {t("patientPage_triggers_manage_title")}
        </ManageTriggersTitle>
      )}
      <ImageContainer>
        <NoAlertTriggeredImage width={181} height={158} />
        <NoContentMessage>{t("patientPage_alerts_empty")}</NoContentMessage>
      </ImageContainer>
    </>
  );
}
